// src/pages/Admin/UserActivity.jsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Stack,
  alpha,
  Switch,
  FormControlLabel,
  TextField,
  InputAdornment,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Circle as CircleIcon,
  Search as SearchIcon,
  Person as PersonIcon,
  AdminPanelSettings as AdminIcon,
  Shield as ShieldIcon,
} from '@mui/icons-material';
import axios from 'axios';
import API_URLS from '../../config/api';

// ==================== HELPERS ====================

const getRelativeTime = (timestamp) => {
  if (!timestamp) return 'Never';
  const now = new Date();
  const then = new Date(timestamp);
  const diffMs = now - then;
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHr = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHr / 24);

  if (diffSec < 60) return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHr < 24) return `${diffHr}h ${diffMin % 60}m ago`;
  if (diffDay < 7) return `${diffDay}d ago`;
  return then.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatDateTime = (timestamp) => {
  if (!timestamp) return '—';
  const d = new Date(timestamp);
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  });
};

const STATUS_CONFIG = {
  online: { label: 'Online', color: '#00e676', bgColor: 'rgba(0, 230, 118, 0.12)', pulseColor: '#00e676' },
  recently_active: { label: 'Recently Active', color: '#ffab40', bgColor: 'rgba(255, 171, 64, 0.12)', pulseColor: '#ffab40' },
  today: { label: 'Today', color: '#42a5f5', bgColor: 'rgba(66, 165, 245, 0.12)', pulseColor: '#42a5f5' },
  inactive: { label: 'Inactive', color: '#78909c', bgColor: 'rgba(120, 144, 156, 0.10)', pulseColor: '#78909c' },
  never: { label: 'Never', color: '#546e7a', bgColor: 'rgba(84, 110, 122, 0.08)', pulseColor: '#546e7a' },
};

const ROLE_CONFIG = {
  admin: { label: 'Admin', icon: AdminIcon, color: '#ff6e40', bgColor: 'rgba(255, 110, 64, 0.12)' },
  officer: { label: 'Officer', icon: ShieldIcon, color: '#00d4ff', bgColor: 'rgba(0, 212, 255, 0.12)' },
};

// ==================== STYLES ====================

const styles = {
  container: {
    p: { xs: 2, md: 3 },
    maxWidth: '1400px',
    mx: 'auto',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    mb: 3,
    flexWrap: 'wrap',
    gap: 2,
  },
  statsRow: {
    display: 'flex',
    gap: 2,
    mb: 3,
    flexWrap: 'wrap',
  },
  statCard: {
    bgcolor: alpha('#fff', 0.03),
    border: '1px solid',
    borderColor: alpha('#fff', 0.06),
    borderRadius: 2,
    px: 2.5,
    py: 1.5,
    minWidth: 140,
    flex: '1 1 140px',
  },
  tableContainer: {
    bgcolor: alpha('#fff', 0.02),
    border: '1px solid',
    borderColor: alpha('#fff', 0.06),
    borderRadius: 2,
    overflow: 'hidden',
  },
  tableHead: {
    bgcolor: alpha('#fff', 0.04),
    '& th': {
      color: alpha('#fff', 0.5),
      fontWeight: 600,
      fontSize: '0.75rem',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      borderBottom: '1px solid',
      borderColor: alpha('#fff', 0.06),
      py: 1.5,
      whiteSpace: 'nowrap',
    },
  },
  tableRow: {
    '&:hover': {
      bgcolor: alpha('#fff', 0.03),
    },
    '& td': {
      borderBottom: '1px solid',
      borderColor: alpha('#fff', 0.04),
      py: 1.5,
      color: alpha('#fff', 0.8),
      fontSize: '0.875rem',
    },
  },
};

// ==================== MAIN COMPONENT ====================

const UserActivity = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [lastRefreshed, setLastRefreshed] = useState(null);

  const fetchActivity = useCallback(async () => {
    try {
      const response = await axios.get(API_URLS.USER_ACTIVITY, { withCredentials: true });
      if (response.data.status === 'success') {
        setUsers(response.data.users);
        setLastRefreshed(new Date());
        setError(null);
      }
    } catch (err) {
      console.error('Failed to fetch user activity:', err);
      setError(err.response?.data?.message || 'Failed to fetch user activity');
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial fetch
  useEffect(() => {
    fetchActivity();
  }, [fetchActivity]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(fetchActivity, 30000);
    return () => clearInterval(interval);
  }, [autoRefresh, fetchActivity]);

  // Filter users by search
  const filteredUsers = users.filter((user) => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      user.username?.toLowerCase().includes(q) ||
      user.full_name?.toLowerCase().includes(q) ||
      user.role?.toLowerCase().includes(q)
    );
  });

  // Stats
  const stats = {
    total: users.length,
    online: users.filter((u) => u.activity_status === 'online').length,
    recentlyActive: users.filter((u) => u.activity_status === 'recently_active').length,
    today: users.filter((u) => u.activity_status === 'today').length,
    inactive: users.filter((u) => ['inactive', 'never'].includes(u.activity_status)).length,
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress sx={{ color: '#00d4ff' }} />
      </Box>
    );
  }

  return (
    <Box sx={styles.container}>
      {/* Header */}
      <Box sx={styles.header}>
        <Box>
          <Typography variant="h5" sx={{ color: '#fff', fontWeight: 700, letterSpacing: '-0.5px' }}>
            User Activity
          </Typography>
          <Typography variant="body2" sx={{ color: alpha('#fff', 0.4), mt: 0.5 }}>
            {lastRefreshed ? `Last updated ${getRelativeTime(lastRefreshed.toISOString())}` : 'Loading...'}
          </Typography>
        </Box>

        <Stack direction="row" spacing={1.5} alignItems="center">
          <FormControlLabel
            control={
              <Switch
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                size="small"
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': { color: '#00d4ff' },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#00d4ff' },
                }}
              />
            }
            label={
              <Typography variant="caption" sx={{ color: alpha('#fff', 0.5) }}>
                Auto-refresh
              </Typography>
            }
          />
          <Tooltip title="Refresh now">
            <IconButton onClick={fetchActivity} sx={{ color: alpha('#fff', 0.5), '&:hover': { color: '#00d4ff' } }}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 2, bgcolor: 'rgba(244,67,54,0.08)', color: '#ef5350' }}>
          {error}
        </Alert>
      )}

      {/* Stats Row */}
      <Box sx={styles.statsRow}>
        <Box sx={styles.statCard}>
          <Typography variant="caption" sx={{ color: alpha('#fff', 0.4), textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Total Users
          </Typography>
          <Typography variant="h5" sx={{ color: '#fff', fontWeight: 700, mt: 0.5 }}>
            {stats.total}
          </Typography>
        </Box>
        <Box sx={{ ...styles.statCard, borderColor: alpha('#00e676', 0.2) }}>
          <Typography variant="caption" sx={{ color: alpha('#fff', 0.4), textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Online Now
          </Typography>
          <Typography variant="h5" sx={{ color: '#00e676', fontWeight: 700, mt: 0.5 }}>
            {stats.online}
          </Typography>
        </Box>
        <Box sx={styles.statCard}>
          <Typography variant="caption" sx={{ color: alpha('#fff', 0.4), textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Recently Active
          </Typography>
          <Typography variant="h5" sx={{ color: '#ffab40', fontWeight: 700, mt: 0.5 }}>
            {stats.recentlyActive}
          </Typography>
        </Box>
        <Box sx={styles.statCard}>
          <Typography variant="caption" sx={{ color: alpha('#fff', 0.4), textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Active Today
          </Typography>
          <Typography variant="h5" sx={{ color: '#42a5f5', fontWeight: 700, mt: 0.5 }}>
            {stats.today}
          </Typography>
        </Box>
        <Box sx={styles.statCard}>
          <Typography variant="caption" sx={{ color: alpha('#fff', 0.4), textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Inactive
          </Typography>
          <Typography variant="h5" sx={{ color: '#78909c', fontWeight: 700, mt: 0.5 }}>
            {stats.inactive}
          </Typography>
        </Box>
      </Box>

      {/* Search */}
      <Box sx={{ mb: 2 }}>
        <TextField
          placeholder="Search by name, username, or role..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          size="small"
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: alpha('#fff', 0.3), fontSize: 20 }} />
              </InputAdornment>
            ),
          }}
          sx={{
            maxWidth: 400,
            '& .MuiOutlinedInput-root': {
              bgcolor: alpha('#fff', 0.03),
              color: '#fff',
              fontSize: '0.875rem',
              '& fieldset': { borderColor: alpha('#fff', 0.08) },
              '&:hover fieldset': { borderColor: alpha('#fff', 0.15) },
              '&.Mui-focused fieldset': { borderColor: '#00d4ff' },
            },
            '& .MuiInputBase-input::placeholder': { color: alpha('#fff', 0.3) },
          }}
        />
      </Box>

      {/* Users Table */}
      <TableContainer component={Paper} elevation={0} sx={styles.tableContainer}>
        <Table size="small">
          <TableHead>
            <TableRow sx={styles.tableHead}>
              <TableCell>User</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Last Active</TableCell>
              <TableCell>Last Login</TableCell>
              <TableCell>Member Since</TableCell>
              <TableCell align="center">Account</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredUsers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                  <PersonIcon sx={{ fontSize: 40, color: alpha('#fff', 0.15), mb: 1 }} />
                  <Typography variant="body2" sx={{ color: alpha('#fff', 0.3) }}>
                    {searchQuery ? 'No users match your search' : 'No users found'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredUsers.map((user) => {
                const statusConfig = STATUS_CONFIG[user.activity_status] || STATUS_CONFIG.never;
                const roleConfig = ROLE_CONFIG[user.role] || ROLE_CONFIG.officer;
                const RoleIcon = roleConfig.icon;

                return (
                  <TableRow key={user.id} sx={styles.tableRow}>
                    {/* User */}
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box
                          sx={{
                            width: 36,
                            height: 36,
                            borderRadius: '50%',
                            bgcolor: alpha(roleConfig.color, 0.15),
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            position: 'relative',
                            flexShrink: 0,
                          }}
                        >
                          <Typography sx={{ color: roleConfig.color, fontWeight: 700, fontSize: '0.8rem' }}>
                            {(user.full_name || user.username || '?').charAt(0).toUpperCase()}
                          </Typography>
                          {/* Online dot indicator */}
                          {user.activity_status === 'online' && (
                            <Box
                              sx={{
                                position: 'absolute',
                                bottom: 0,
                                right: 0,
                                width: 10,
                                height: 10,
                                borderRadius: '50%',
                                bgcolor: '#00e676',
                                border: '2px solid #0a0e14',
                              }}
                            />
                          )}
                        </Box>
                        <Box>
                          <Typography sx={{ color: '#fff', fontWeight: 600, fontSize: '0.875rem', lineHeight: 1.3 }}>
                            {user.full_name || user.username}
                          </Typography>
                          <Typography sx={{ color: alpha('#fff', 0.35), fontSize: '0.75rem' }}>
                            @{user.username}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>

                    {/* Role */}
                    <TableCell>
                      <Chip
                        icon={<RoleIcon sx={{ fontSize: 14, color: `${roleConfig.color} !important` }} />}
                        label={roleConfig.label}
                        size="small"
                        sx={{
                          bgcolor: roleConfig.bgColor,
                          color: roleConfig.color,
                          fontWeight: 600,
                          fontSize: '0.7rem',
                          height: 26,
                          border: '1px solid',
                          borderColor: alpha(roleConfig.color, 0.2),
                          '& .MuiChip-icon': { ml: '6px' },
                        }}
                      />
                    </TableCell>

                    {/* Activity Status */}
                    <TableCell>
                      <Chip
                        icon={
                          <CircleIcon
                            sx={{
                              fontSize: 8,
                              color: `${statusConfig.color} !important`,
                              animation: user.activity_status === 'online' ? 'pulse 2s infinite' : 'none',
                              '@keyframes pulse': {
                                '0%': { opacity: 1 },
                                '50%': { opacity: 0.4 },
                                '100%': { opacity: 1 },
                              },
                            }}
                          />
                        }
                        label={statusConfig.label}
                        size="small"
                        sx={{
                          bgcolor: statusConfig.bgColor,
                          color: statusConfig.color,
                          fontWeight: 600,
                          fontSize: '0.7rem',
                          height: 26,
                          border: '1px solid',
                          borderColor: alpha(statusConfig.color, 0.15),
                          '& .MuiChip-icon': { ml: '6px' },
                        }}
                      />
                    </TableCell>

                    {/* Last Active */}
                    <TableCell>
                      <Tooltip title={formatDateTime(user.last_activity)} arrow placement="top">
                        <Typography
                          sx={{
                            fontSize: '0.8rem',
                            color: user.activity_status === 'online' ? '#00e676' : alpha('#fff', 0.6),
                            fontWeight: user.activity_status === 'online' ? 600 : 400,
                            cursor: 'default',
                          }}
                        >
                          {user.last_activity ? getRelativeTime(user.last_activity) : '—'}
                        </Typography>
                      </Tooltip>
                    </TableCell>

                    {/* Last Login */}
                    <TableCell>
                      <Tooltip title={formatDateTime(user.last_login)} arrow placement="top">
                        <Typography sx={{ fontSize: '0.8rem', color: alpha('#fff', 0.5), cursor: 'default' }}>
                          {user.last_login ? getRelativeTime(user.last_login) : '—'}
                        </Typography>
                      </Tooltip>
                    </TableCell>

                    {/* Member Since */}
                    <TableCell>
                      <Typography sx={{ fontSize: '0.8rem', color: alpha('#fff', 0.4) }}>
                        {user.created_at
                          ? new Date(user.created_at).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                            })
                          : '—'}
                      </Typography>
                    </TableCell>

                    {/* Account Status */}
                    <TableCell align="center">
                      <Chip
                        label={user.is_active ? 'Active' : 'Disabled'}
                        size="small"
                        sx={{
                          bgcolor: user.is_active ? 'rgba(0, 230, 118, 0.08)' : 'rgba(244, 67, 54, 0.08)',
                          color: user.is_active ? '#66bb6a' : '#ef5350',
                          fontWeight: 600,
                          fontSize: '0.7rem',
                          height: 24,
                          border: '1px solid',
                          borderColor: user.is_active ? alpha('#66bb6a', 0.2) : alpha('#ef5350', 0.2),
                        }}
                      />
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Footer */}
      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="caption" sx={{ color: alpha('#fff', 0.3) }}>
          {filteredUsers.length} of {users.length} users
          {searchQuery && ` matching "${searchQuery}"`}
        </Typography>
        <Typography variant="caption" sx={{ color: alpha('#fff', 0.2) }}>
          Activity updates every 5 minutes per user &middot; Dashboard refreshes every 30 seconds
        </Typography>
      </Box>
    </Box>
  );
};

export default UserActivity;
